import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  QrCode,
  Download,
  Printer,
  Package,
  Search,
  CheckSquare,
  Square,
  FileDown,
  ChevronDown,
} from "lucide-react";
import { useAssets, useAssetCategories, useEntities } from "../hooks/useAssets";
import { Button, Spinner, Badge } from "../components/ui/index.jsx";
import { StatusBadge } from "../components/asset/AssetHelpers.jsx";
import { ProvisionalNumberWarning } from "../components/asset/ProvisionalNumberWarning.jsx";
import apiClient from "../api/client";
import {
  loadFonts,
  renderHorizontalLabel,
  renderVerticalLabel,
  renderPrintSheet,
  downloadCanvasAsJpeg,
  printCanvas,
} from "../utils/labelRenderer";

// ---------------------------------------------------------------------------
// Label preview modal
// ---------------------------------------------------------------------------
function LabelPreviewModal({ label, onClose }) {
  const [hCanvas, setHCanvas] = useState(null);
  const [vCanvas, setVCanvas] = useState(null);
  const [loading, setLoading] = useState(true);

  useState(() => {
    (async () => {
      await loadFonts();
      const [h, v] = await Promise.all([
        renderHorizontalLabel(label),
        renderVerticalLabel(label),
      ]);
      setHCanvas(h);
      setVCanvas(v);
      setLoading(false);
    })();
  }, []);

  if (!label) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 z-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-900">
            Label Preview — {label.assetNumber}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        <ProvisionalNumberWarning className="mb-4" />

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Horizontal */}
            <div>
              <p className="text-xs text-slate-500 mb-2 font-medium">
                HORIZONTAL — 75×20mm
              </p>
              <div className="border border-slate-200 rounded overflow-hidden">
                <img src={hCanvas} alt="Horizontal label" className="w-full" />
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = hCanvas;
                    a.download = `${label.assetNumber}-75x20.jpg`;
                    a.click();
                  }}
                >
                  <Download className="h-3.5 w-3.5" /> Download JPEG
                </Button>
              </div>
            </div>

            {/* Vertical */}
            <div>
              <p className="text-xs text-slate-500 mb-2 font-medium">
                VERTICAL — 15×20mm
              </p>
              <div className="border border-slate-200 rounded overflow-hidden inline-block">
                <img
                  src={vCanvas}
                  alt="Vertical label"
                  style={{ width: 120 }}
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = vCanvas;
                    a.download = `${label.assetNumber}-15x20.jpg`;
                    a.click();
                  }}
                >
                  <Download className="h-3.5 w-3.5" /> Download JPEG
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function LabelsPage() {
  const navigate = useNavigate();

  // Filters
  const [params, setParams] = useState({
    page: 1,
    limit: 50,
    search: "",
    entityId: "",
    categoryId: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const { data, isLoading } = useAssets(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "")),
  );
  const { data: entities = [] } = useEntities();
  const { data: categories = [] } = useAssetCategories();

  const assets = data?.data ?? [];
  const pagination = data?.pagination;

  // Selection
  const [selected, setSelected] = useState(new Set());
  const allSelected =
    assets.length > 0 && assets.every((a) => selected.has(a.id));

  function toggleOne(id) {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function toggleAll() {
    if (allSelected) {
      setSelected((s) => {
        const n = new Set(s);
        assets.forEach((a) => n.delete(a.id));
        return n;
      });
    } else {
      setSelected((s) => {
        const n = new Set(s);
        assets.forEach((a) => n.add(a.id));
        return n;
      });
    }
  }

  // Label preview state
  const [previewLabel, setPreviewLabel] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);

  // Bulk action state
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pageSize, setPageSize] = useState("A4");
  const [labelFormat, setLabelFormat] = useState("horizontal");

  async function fetchLabelData(assetIds) {
    const { data } = await apiClient.post("/api/labels/bulk", { assetIds });
    return data.labels;
  }

  async function handlePreview(asset) {
    setLoadingPreview(asset.id);
    try {
      await loadFonts();
      const { data } = await apiClient.get(`/api/labels/${asset.id}`);
      setPreviewLabel(data);
    } finally {
      setLoadingPreview(null);
    }
  }

  async function handlePrintSheet() {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await loadFonts();
      const labels = await fetchLabelData([...selected]);
      const canvas = await renderPrintSheet(labels, pageSize, labelFormat);
      printCanvas(canvas);
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleDownloadSheet() {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const labels = await fetchLabelData([...selected]);
      const canvas = await renderPrintSheet(labels, pageSize, labelFormat);
      downloadCanvasAsJpeg(
        canvas,
        `labels-sheet-${pageSize}-${Date.now()}.jpg`,
      );
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleBulkZip() {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const labels = await fetchLabelData([...selected]);

      // Render all labels
      const renders = await Promise.all(
        labels.flatMap((label) => [
          renderHorizontalLabel(label).then((url) => ({
            name: `${label.assetNumber}-75x20.jpg`,
            url,
          })),
          renderVerticalLabel(label).then((url) => ({
            name: `${label.assetNumber}-15x20.jpg`,
            url,
          })),
        ]),
      );

      // Zip using JSZip
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      for (const { name, url } of renders) {
        // Convert data URL to blob
        const res = await fetch(url);
        const blob = await res.blob();
        zip.file(name, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(zipBlob);
      a.download = `labels-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setBulkLoading(false);
    }
  }

  function setFilter(key, value) {
    setParams((p) => ({ ...p, [key]: value, page: 1 }));
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Print Labels</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Select assets to generate QR labels for printing
          </p>
        </div>
      </div>

      <ProvisionalNumberWarning className="mb-4" />

      {/* Bulk action bar — shows when items selected */}
      {selected.size > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-primary-800">
            {selected.size} asset{selected.size !== 1 ? "s" : ""} selected
          </span>

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {/* Page size */}
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              className="text-sm border border-primary-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none"
            >
              <option value="A4">A4</option>
              <option value="A3">A3</option>
            </select>

            {/* Label format */}
            <select
              value={labelFormat}
              onChange={(e) => setLabelFormat(e.target.value)}
              className="text-sm border border-primary-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none"
            >
              <option value="horizontal">75×20mm only</option>
              <option value="vertical">15×20mm only</option>
              <option value="both">Both formats</option>
            </select>

            <Button
              size="sm"
              variant="secondary"
              onClick={handlePrintSheet}
              isLoading={bulkLoading}
            >
              <Printer className="h-4 w-4" /> Print Sheet
            </Button>

            <Button
              size="sm"
              variant="secondary"
              onClick={handleDownloadSheet}
              isLoading={bulkLoading}
            >
              <FileDown className="h-4 w-4" /> Download Sheet
            </Button>

            <Button size="sm" onClick={handleBulkZip} isLoading={bulkLoading}>
              <Download className="h-4 w-4" /> Download ZIP
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFilter("search", searchInput);
          }}
          className="flex gap-2 flex-1 min-w-48"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            Search
          </Button>
        </form>

        <select
          value={params.entityId}
          onChange={(e) => setFilter("entityId", e.target.value)}
          className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Entities</option>
          {entities.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} — {e.name}
            </option>
          ))}
        </select>

        <select
          value={params.categoryId}
          onChange={(e) => setFilter("categoryId", e.target.value)}
          className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No assets found.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 w-10">
                  <button onClick={toggleAll}>
                    {allSelected ? (
                      <CheckSquare className="h-4 w-4 text-primary-600" />
                    ) : (
                      <Square className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  Asset Number
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  Entity
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">
                  Labels
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {assets.map((asset) => (
                <tr
                  key={asset.id}
                  className={`transition-colors ${selected.has(asset.id) ? "bg-primary-50" : "hover:bg-slate-50"}`}
                >
                  <td className="px-4 py-3">
                    <button onClick={() => toggleOne(asset.id)}>
                      {selected.has(asset.id) ? (
                        <CheckSquare className="h-4 w-4 text-primary-600" />
                      ) : (
                        <Square className="h-4 w-4 text-slate-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">
                    {asset.assetNumber}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900 truncate max-w-48">
                      {asset.name}
                    </p>
                    <p className="text-xs text-slate-400">
                      {asset.category?.name}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {asset.entity?.code}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={asset.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="secondary"
                      isLoading={loadingPreview === asset.id}
                      onClick={() => handlePreview(asset)}
                    >
                      <QrCode className="h-3.5 w-3.5" /> Preview
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setParams((p) => ({ ...p, page: p.page - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setParams((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Label preview modal */}
      {previewLabel && (
        <LabelPreviewModal
          label={previewLabel}
          onClose={() => setPreviewLabel(null)}
        />
      )}
    </div>
  );
}
